import {
  DISCOUNT_TYPE,
  LICENSE_TYPE,
  PAYMENT_METHOD,
  PRODUCT_TYPE,
} from './utilityFunctions';

// const ALLOW_BANK_TRANSFER = 200000;

const getMaximumRangeAllowed = (priceTriers = []) =>
  priceTriers.reduce((acc, p) => {
    if (p.maximumRange > acc) {
      return p.maximumRange;
    }
    return acc;
  }, 0);

const getPriceForUsers = (priceTriers = [], totalUsers = 0) => {
  const priceInfo = priceTriers.find(p => {
    if (p.minimumRange <= totalUsers) {
      if (!p.maximumRange || p.maximumRange >= totalUsers) {
        return true;
      }
    }
    return false;
  }) || { unitAmount: 0 };

  const isFlat = !!priceInfo.flatAmount;
  const hasMinimumFlat = isFlat && priceInfo.unitAmount > 0;
  if (hasMinimumFlat) {
    const minimumFlat = priceInfo.flatAmount;
    const totalCost = totalUsers * priceInfo.unitAmount;
    return {
      price: minimumFlat >= totalCost ? minimumFlat : priceInfo.unitAmount,
      isFlat: minimumFlat >= totalCost,
      total: minimumFlat >= totalCost ? minimumFlat : totalCost,
    };
  }
  return {
    price: isFlat ? priceInfo.flatAmount : priceInfo.unitAmount,
    isFlat,
    total: isFlat ? priceInfo.flatAmount : totalUsers * priceInfo.unitAmount,
  };
};

const getPriceOfProduct = (product, totalUsers = 0) => {
  const { price, tiersUsers, type } = product;
  if (tiersUsers?.length > 0) {
    const priceInfo = tiersUsers.find(p => {
      if (p.minimumRange <= totalUsers) {
        if (!p.maximumRange || p.maximumRange >= totalUsers) {
          return true;
        }
      }
      return false;
    }) || { unitAmount: 0 };

    const isFlat = !!priceInfo.flatAmount;
    const hasMinimumFlat = isFlat && priceInfo.unitAmount > 0;
    if (hasMinimumFlat) {
      const minimumFlat = priceInfo.flatAmount;
      const totalCost = totalUsers * priceInfo.unitAmount;
      return {
        price: minimumFlat >= totalCost ? minimumFlat : priceInfo.unitAmount,
        isFlat: minimumFlat >= totalCost,
        total: minimumFlat >= totalCost ? minimumFlat : totalCost,
      };
    }
    return {
      price: isFlat ? priceInfo.flatAmount : priceInfo.unitAmount,
      isFlat,
      total: isFlat ? priceInfo.flatAmount : totalUsers * priceInfo.unitAmount,
    };
  }
  // Old products behaviors
  const isSubscription =
    type === PRODUCT_TYPE.SUBSCRIPTION ||
    type === PRODUCT_TYPE.PREMIUM_SUBSCRIPTION;
  return {
    price,
    isFlat: !isSubscription,
    total: !isSubscription ? price : price * totalUsers,
  };
};

const applyDiscount = (total, discounts) => {
  let result = total;
  const discountApplications = discounts.map(discount => {
    let applied = false;
    const { amountOff, percentOff } = discount;

    const minPrice =
      discount.minPrice === null || discount.minPrice === undefined
        ? 0
        : discount.minPrice;
    const maxPrice =
      discount.maxPrice === null || discount.maxPrice === undefined
        ? Number.MAX_SAFE_INTEGER
        : discount.maxPrice;

    if (total >= minPrice && total <= maxPrice) {
      applied = true;
      if (amountOff) {
        result -= discount.amountOff;
      } else if (percentOff) {
        // Make sure we're not using money amount below cent
        result -= Math.round(total * (percentOff / 100));
      }
    }
    return { ...discount, applied };
  });

  if (result < 0) {
    result = 0;
  }
  return { result, discountApplications };
};

function calcPlansTotal(cart, plans, plansAreFree) {
  const { plan: cartPlan, numberUsers, numberAdmins } = cart;
  const { tiersUsers, tiersAdmins } = cartPlan;
  const { total: totalPriceUsers } = getPriceForUsers(tiersUsers, numberUsers);
  const { total: totalPriceAdmins } = getPriceForUsers(
    tiersAdmins,
    numberAdmins,
  );
  return plansAreFree ? 0 : totalPriceUsers + totalPriceAdmins;
}

function calcSubscriptionsTotal(cart, plansAreFree) {
  const { subscriptions, numberUsers } = cart;

  return subscriptions.reduce((acc, { product }) => {
    let finalPrice = 0;
    if (!(plansAreFree && product.isFreeTrial)) {
      const { total } = getPriceOfProduct(product, numberUsers);
      finalPrice = total;
    }
    return acc + finalPrice;
  }, 0);
}

const splitDiscounts = discounts =>
  discounts.reduce(
    ({ subs, prods }, discount) => {
      if (discount.type === DISCOUNT_TYPE.SUBSCRIPTION) {
        return { subs: subs.concat(discount), prods };
      }
      return { subs, prods: prods.concat(discount) };
    },
    { subs: [], prods: [] },
  );

const calcTax = (total, taxRatePercentage) =>
  Math.round(total * (taxRatePercentage / 100));

const calcMonthlyTotal = (cart, plans, plansAreFree) =>
  applyDiscount(
    calcPlansTotal(cart, plans, plansAreFree) +
      calcSubscriptionsTotal(cart, plansAreFree),
    splitDiscounts(cart.discounts).subs,
  ).result;

const calcMonthlyPostTax = (cart, plans, plansAreFree, taxRatePercentage) => {
  const preTax = calcMonthlyTotal(cart, plans, plansAreFree);
  const tax = calcTax(preTax, taxRatePercentage);
  return preTax + tax;
};

const splitProducts = (cart, cartProdDiscounts, categories) => {
  const { products: cartProds, packs: cartPacks } = cart;

  return cartProds.concat(cartPacks).reduce(
    (acc, productInfo) => {
      const { _id: productId } = productInfo.product;

      for (const discount of cartProdDiscounts) {
        const hasProducts = discount.products.length !== 0;
        const hasProductCategories = discount.productCategories.length !== 0;

        if (!hasProducts && !hasProductCategories) {
          return {
            discounted: acc.discounted.concat(productInfo),
            other: acc.other,
          };
        }
        if (hasProducts && discount.products.includes(productId)) {
          return {
            discounted: acc.discounted.concat(productInfo),
            other: acc.other,
          };
        }
        if (hasProductCategories) {
          for (const categoryId of discount.productCategories) {
            // in case the product is a pack
            if (categoryId === productId) {
              return {
                discounted: acc.discounted.concat(productInfo),
                other: acc.other,
              };
            }
            const categoryProductIds = (
              categories.find(c => c._id === categoryId) || { products: [] }
            ).products;
            if (categoryProductIds.some(p => p._id === productId)) {
              return {
                discounted: acc.discounted.concat(productInfo),
                other: acc.other,
              };
            }
          }
        }
      }
      return {
        discounted: acc.discounted,
        other: acc.other.concat(productInfo),
      };
    },
    { discounted: [], other: [] },
  );
};

function sumCart(acc, { product, quantity }) {
  const price = quantity * product.price;
  return acc + price;
}

const calcCurrentTotal = (cart, categories) => {
  const { prods: cartProdDiscounts } = splitDiscounts(cart.discounts);
  const { discounted, other } = splitProducts(
    cart,
    cartProdDiscounts,
    categories,
  );

  const discountedTotal = discounted.reduce(sumCart, 0);
  const otherTotal = other.reduce(sumCart, 0);

  return applyDiscount(discountedTotal, cartProdDiscounts).result + otherTotal;
};

const calcCurrentPostTax = (cart, categories, taxRatePercentage) => {
  const preTax = calcCurrentTotal(cart, categories);
  const tax = calcTax(preTax, taxRatePercentage);
  return preTax + tax;
};

const markDiscountApplication = (cart, categories, plans, plansAreFree) =>
  cart.discounts.map(discount => {
    if (discount.type === DISCOUNT_TYPE.SUBSCRIPTION) {
      const { discountApplications } = applyDiscount(
        calcPlansTotal(cart, plans, plansAreFree) +
          calcSubscriptionsTotal(cart, plansAreFree),
        [discount],
      );
      return { ...discount, ...discountApplications[0] };
    }

    // Discount of type product
    let applied = false;
    const { discounted } = splitProducts(cart, [discount], categories);
    if (discounted.length > 0) {
      // if the discount doesn't apply to any products than no need to go any further
      const discountedTotal = discounted.reduce(
        (acc, { product, quantity }) => {
          const price = quantity * product.price;
          return acc + price;
        },
        0,
      );

      const { discountApplications } = applyDiscount(discountedTotal, [
        discount,
      ]);

      [{ applied }] = discountApplications;
    }
    return { ...discount, applied };
  });

const allowBankTransferPaymentMethod = (
  cart,
  plans,
  categories,
  taxRatePercentage,
  plansAreFree,
  isSuperAdmin = false,
) => {
  const { plan: cartPlan } = cart;
  return (
    isSuperAdmin &&
    (cartPlan.licenseType === LICENSE_TYPE.ENTERPRISE ||
      cartPlan.licenseType === LICENSE_TYPE.RESELLER_WEMANITY)
  );
  // return (
  //   calcMonthlyPostTax(cart, plans, plansAreFree, taxRatePercentage) >
  //     ALLOW_BANK_TRANSFER ||
  //   calcCurrentPostTax(cart, categories, taxRatePercentage) >
  //     ALLOW_BANK_TRANSFER
  // );
};

const isValidPaymentMethod = (
  cart,
  plans,
  categories,
  taxRatePercentage,
  plansAreFree,
  paymentMethodType,
  paymentMethodId,
  isResellerInstance,
  isSuperAdmin,
) => {
  if (isResellerInstance) {
    return true;
  }
  if (plansAreFree) {
    return true;
  }
  switch (paymentMethodType) {
    case PAYMENT_METHOD.CREDIT_CARD:
    case PAYMENT_METHOD.BANCONTACT:
    case PAYMENT_METHOD.PAYPAL:
    case PAYMENT_METHOD.SEPA_DEBIT:
      return !!paymentMethodId;
    case PAYMENT_METHOD.BANK_TRANSFER_MANUAL:
      return allowBankTransferPaymentMethod(
        cart,
        plans,
        categories,
        taxRatePercentage,
        plansAreFree,
        isSuperAdmin,
      );
    case PAYMENT_METHOD.NONE:
      return false;
    default:
      return !!paymentMethodId;
  }
};

const isFeatureAvailable = (product, selectedPlan) => {
  const { requiredLicenseTypes = [], requiredPlans = [] } = product;
  const { type: planType, licenseType } = selectedPlan;
  const featureAvailablePerPlan =
    requiredPlans.length === 0 || !!requiredPlans.find(p => p === planType);
  const featureAvailablePerLicenseType =
    requiredLicenseTypes.length === 0 ||
    !!requiredLicenseTypes.find(p => p === licenseType);
  return featureAvailablePerPlan && featureAvailablePerLicenseType;
};

export {
  allowBankTransferPaymentMethod,
  isValidPaymentMethod,
  getMaximumRangeAllowed,
  getPriceForUsers,
  getPriceOfProduct,
  applyDiscount,
  splitDiscounts,
  splitProducts,
  calcTax,
  calcMonthlyTotal,
  calcMonthlyPostTax,
  calcCurrentTotal,
  calcCurrentPostTax,
  markDiscountApplication,
  isFeatureAvailable,
};
